import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { SigningServiceNotice } from '../../components/SigningServiceNotice';
import { EmptyState, Loading } from '../../components/States';
import { loadSigningService } from '../../lib/signingService';
import { useApi } from '../../lib/useApi';
import { SignatureSettingsForm } from '../compliance/vnnic/RegistrarSignaturePage';
import { PendingChangesPanel, PROVIDER_CHANGE_KINDS } from './changes';
import { DeploymentNote, useIntegrationContext, WithIntegrationContext } from './shared';
import { ProviderPanel } from './VnnicPage';

/**
 * Cài đặt › Kết nối › SmartCA: VNPT SmartCA, the registrar's signing service — the connection
 * (URL, SP ID, SP secret stored encrypted, the signer's SmartCA account, the signature text; "Kiểm
 * tra kết nối" reads the signer's certificate) and the signing service with the daily batch time.
 */
export function SmartCaPage() {
  const { t } = useTranslation('integrations');
  useDocumentTitle(t('provider.tab.VNPT_SMARTCA'));
  return (
    <>
      <PageHeader title={t('provider.name.VNPT_SMARTCA')} subtitle={t('provider.description.VNPT_SMARTCA')} />
      <WithIntegrationContext>
        <SmartCaView />
      </WithIntegrationContext>
    </>
  );
}

function SmartCaView() {
  const { t } = useTranslation('integrations');
  const { t: tc } = useTranslation('compliance');
  const ctx = useIntegrationContext();
  const [tick, setTick] = useState(0);
  const catalog = useApi((signal) => api.getProviderCatalog({ signal }), []);
  const service = useApi((signal) => loadSigningService(signal), [tick]);
  const batches = useApi((signal) => api.listSignatureBatches({ signal }), [tick]);
  const reload = () => setTick((n) => n + 1);
  const def = catalog.status === 'success' ? catalog.data.providers.find((p) => p.provider === 'VNPT_SMARTCA') : undefined;
  return (
    <div className="space-y-6" data-smartca-page>
      <DeploymentNote providers />
      <SigningServiceNotice service={service.status === 'success' ? service.data : null} />
      <PendingChangesPanel kinds={PROVIDER_CHANGE_KINDS} refreshKey={tick} onChanged={reload} />
      {catalog.status === 'error' ? <DataError error={catalog.error} onRetry={catalog.reload} title={t('vnnic.catalogFailed')} /> : null}
      {catalog.status === 'loading' ? <Loading rows={3} /> : null}
      {catalog.status === 'success' ? def ? <ProviderPanel def={def} env={ctx.integration_environment} tick={tick} onChanged={reload} /> : <EmptyState title={t('vnnic.unknownProvider')} /> : null}
      <Section title={tc('signature.settings.title')}>
        {batches.status === 'loading' ? <Loading rows={2} /> : null}
        {batches.status === 'error' ? <DataError error={batches.error} onRetry={batches.reload} /> : null}
        {batches.status === 'success' ? <SignatureSettingsForm key={JSON.stringify(batches.data.settings)} initial={batches.data.settings} onSaved={reload} /> : null}
      </Section>
    </div>
  );
}
